import { type Person } from "@/entities/person";
import { type Maintenance, type MaintenanceTask } from "@entities/maintenance/types";
import { MAINTENANCE_TYPE, WorkflowStatusConstants } from "@/features/maintenance/constants";

function generateMaintenancesForAsset(
  assetId: string,
  count: number,
  persons: Person[]
): { maintenances: Maintenance[]; tasks: MaintenanceTask[] } {
  const maintenances: Maintenance[] = [];
  const tasks: MaintenanceTask[] = [];
  const taskTypes: Array<
    "checkbox" | "measurement" | "reading" | "inspection" | "refill_fluid" | "scan_barcode"
  > = ["checkbox", "measurement", "reading", "inspection", "refill_fluid", "scan_barcode"];

  const startDate = new Date();
  startDate.setFullYear(startDate.getFullYear() - 2.5); // Start 2.5 years ago

  const statusDistribution = [
    { status: "completed", count: 200 },
    { status: "pending", count: 200 },
    { status: "in_progress", count: 100 },
    { status: "pending", count: 100, overdue: true }, // Overdue
  ];

  let maintenanceIndex = 0;

  for (const dist of statusDistribution) {
    for (let i = 0; i < dist.count && maintenanceIndex < count; i++, maintenanceIndex++) {
      const scheduledDate = new Date(startDate);
      scheduledDate.setDate(scheduledDate.getDate() + maintenanceIndex * 3); // Every 3 days

      const isOverdue = dist.overdue === true;
      const overdueDays = Math.floor(Math.random() * 30) + 1;

      const maintenance: Maintenance = {
        id: `mnt-large-${maintenanceIndex}`,
        accountId: "1",
        assetId,
        title: `Wartung ${maintenanceIndex + 1} - ${["Inspektion", "Ölwechsel", "Filter tauschen", "Schmierung", "Sichtinspektion"][Math.floor(Math.random() * 5)]}`,
        description: `Planmäßige Wartung gemäß Wartungsplan`,
        type: Math.random() > 0.3 ? MAINTENANCE_TYPE.MAINTENANCE : MAINTENANCE_TYPE.INSPECTION,
        workflowStatus:
          dist.status === "completed"
            ? WorkflowStatusConstants.COMPLETED
            : WorkflowStatusConstants.OPEN,
        scheduledDate: scheduledDate.toISOString().split("T")[0],
        completedDate:
          dist.status === "completed"
            ? new Date(scheduledDate.getTime() + Math.random() * 7 * 24 * 60 * 60 * 1000)
                .toISOString()
                .split("T")[0]
            : undefined,
        durationDays: Math.floor(Math.random() * 3),
        durationHours: Math.floor(Math.random() * 8) + 1,
        durationMinutes: Math.floor(Math.random() * 60),
        intervalType: ["weekly", "monthly", "yearly"][Math.floor(Math.random() * 3)] as
          | "weekly"
          | "monthly"
          | "yearly",
        intervalNumber: Math.floor(Math.random() * 4) + 1,
        intervalMode: "fixed_date",
        assignedToId: persons[Math.floor(Math.random() * persons.length)].id,
        overdueDays: isOverdue ? overdueDays : undefined,
        isExternal: false,
        areTasksPaused: false,
        createdAt: scheduledDate.toISOString(),
        updatedAt: new Date().toISOString(),
      };

      maintenances.push(maintenance);

      // Generate 7 tasks per maintenance
      for (let t = 0; t < 7; t++) {
        const taskType = taskTypes[t % taskTypes.length];
        const isMandatory = t < 3; // First 3 tasks are mandatory
        const isCompleted =
          dist.status === "completed" || (dist.status === "in_progress" && Math.random() > 0.5);

        tasks.push({
          id: `task-large-${maintenanceIndex}-${t}`,
          maintenanceId: maintenance.id,
          name: `Aufgabe ${t + 1}: ${["Inspektion", "Messung", "Sichtinspektion", "Dokumentation", "Reinigung", "Austausch", "Test"][t]}`,
          description: `${taskType === "measurement" ? "Messwert erfassen" : taskType === "reading" ? "Ablesewert dokumentieren" : "Durchführung und Dokumentation"}`,
          type: taskType,
          isMandatory: isMandatory,
          targetValue: ["measurement", "reading", "refill_fluid"].includes(taskType)
            ? Math.floor(Math.random() * 100) + 1
            : undefined,
          actualValue:
            isCompleted && ["measurement", "reading", "refill_fluid"].includes(taskType)
              ? Math.floor(Math.random() * 100) + 1
              : undefined,
          completedAt: isCompleted
            ? new Date(scheduledDate.getTime() + Math.random() * 7 * 24 * 60 * 60 * 1000)
            : undefined,
          createdAt: scheduledDate.toISOString(),
          updatedAt: new Date().toISOString(),
        });
      }
    }
  }

  return { maintenances, tasks };
}
const personFirstNames = [
  "Michael",
  "Thomas",
  "Andreas",
  "Stefan",
  "Christian",
  "Markus",
  "Daniel",
  "Martin",
  "Klaus",
  "Jürgen",
  "Peter",
  "Frank",
  "Uwe",
  "Werner",
  "Hans",
];

const personLastNames = [
  "Müller",
  "Schmidt",
  "Schneider",
  "Fischer",
  "Weber",
  "Meyer",
  "Wagner",
  "Becker",
  "Schulz",
  "Hoffmann",
  "Schäfer",
  "Koch",
  "Bauer",
  "Richter",
  "Klein",
];

const STRESS_TEST_ASSET_ID = "asset-stress-test-1";
function generatePersons(count: number): Person[] {
  const persons: Person[] = [];

  for (let i = 0; i < count; i++) {
    const firstName = personFirstNames[Math.floor(Math.random() * personFirstNames.length)];
    const lastName = personLastNames[Math.floor(Math.random() * personLastNames.length)];
    const isExternal = i >= count / 2; // Second half are external

    persons.push({
      id: `person-large-${i}`,
      tenant_id: "",
      firstname: firstName,
      lastname: lastName,
      email: `${firstName.toLowerCase()}.${lastName.toLowerCase()}@${isExternal ? "extern" : "tti"}.de`,
      phone: `+49 ${Math.floor(Math.random() * 900) + 100} ${Math.floor(Math.random() * 9000000) + 1000000}`,
      company: isExternal ? "Externe Dienstleister GmbH" : "TTI Intralogistik",
      external: isExternal,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    });
  }

  return persons;
}

const persons = generatePersons(30);
const { maintenances: mockMaintenances, tasks: mockMaintenancesTasks } =
  generateMaintenancesForAsset(STRESS_TEST_ASSET_ID, 600, persons);

const demoMaintenanceTaskTemplates = [
  {
    id: "tpl-1",
    accountId: "1",
    tenant_id: "1",
    name: "Backofen Jahresinspektion",
    description: "Jährliche Sicherheits- und Funktionsinspektion gemäß Herstellervorgaben",
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    tasks: [
      {
        id: "tpl-1-t1",
        name: "Brenner und Zünd­einrichtung prüfen",
        type: "checkbox",
        isMandatory: true,
        sortOrder: 1,
        createdAt: new Date().toISOString(),
      },
      {
        id: "tpl-1-t2",
        name: "Türdichtungen auf Verschleiß kontrollieren",
        type: "checkbox",
        isMandatory: true,
        sortOrder: 2,
        createdAt: new Date().toISOString(),
      },
      {
        id: "tpl-1-t3",
        name: "Betriebstemperatur messen",
        type: "measurement",
        isMandatory: true,
        targetValue: 280,
        sortOrder: 3,
        createdAt: new Date().toISOString(),
      },
      {
        id: "tpl-1-t4",
        name: "Sicherheitsventile und Überdruckventile prüfen",
        type: "checkbox",
        isMandatory: true,
        sortOrder: 4,
        createdAt: new Date().toISOString(),
      },
      {
        id: "tpl-1-t5",
        name: "Reinigung Backraum und Heizstäbe",
        type: "checkbox",
        isMandatory: false,
        sortOrder: 5,
        createdAt: new Date().toISOString(),
      },
    ],
  },
  {
    id: "tpl-2",
    accountId: "1",
    tenant_id: "1",
    name: "Kühlgerät Halbjahreswartung",
    description: "Kältemittelstand, Kondensator und Türdichtungen prüfen",
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    tasks: [
      {
        id: "tpl-2-t1",
        name: "Kältemittelstand prüfen",
        type: "checkbox",
        isMandatory: true,
        sortOrder: 1,
        createdAt: new Date().toISOString(),
      },
      {
        id: "tpl-2-t2",
        name: "Kondensator reinigen",
        type: "checkbox",
        isMandatory: true,
        sortOrder: 2,
        createdAt: new Date().toISOString(),
      },
      {
        id: "tpl-2-t3",
        name: "Innentemperatur messen",
        type: "measurement",
        isMandatory: true,
        targetValue: -18,
        sortOrder: 3,
        createdAt: new Date().toISOString(),
      },
      {
        id: "tpl-2-t4",
        name: "Türdichtungen prüfen",
        type: "checkbox",
        isMandatory: true,
        sortOrder: 4,
        createdAt: new Date().toISOString(),
      },
    ],
  },
  {
    id: "tpl-3",
    accountId: "1",
    tenant_id: "1",
    name: "Spülmaschine Quartalsservice",
    description: "Düsen, Dosieranlage und Wasserqualität prüfen",
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    tasks: [
      {
        id: "tpl-3-t1",
        name: "Sprühdüsen auf Verstopfung prüfen",
        type: "checkbox",
        isMandatory: true,
        sortOrder: 1,
        createdAt: new Date().toISOString(),
      },
      {
        id: "tpl-3-t2",
        name: "Dosierpumpe Reiniger prüfen",
        type: "checkbox",
        isMandatory: true,
        sortOrder: 2,
        createdAt: new Date().toISOString(),
      },
      {
        id: "tpl-3-t3",
        name: "Wassertemperatur messen",
        type: "measurement",
        isMandatory: true,
        targetValue: 65,
        sortOrder: 3,
        createdAt: new Date().toISOString(),
      },
      {
        id: "tpl-3-t4",
        name: "Siebe und Filter reinigen",
        type: "checkbox",
        isMandatory: false,
        sortOrder: 4,
        createdAt: new Date().toISOString(),
      },
    ],
  },
];

export { mockMaintenances, mockMaintenancesTasks, demoMaintenanceTaskTemplates };
